import { tm } from '../tm'
import { Ficello } from '../Ficello'
import { ContributionsCount } from '../ContributionsCount'
import { Icon } from '../Icon'
import { ProjectOrganizationLogo } from '../ProjectOrganizationLogo'
import i18n from './i18n'
import { ProjectImpactConversion } from './Conversion'
import type { ReactNode } from 'react'
import type { Locales, Project } from '@captaincause/types'

interface ContainerProps {
  children?: ReactNode
  className?: string
  locale: Locales
  pointsMode?: boolean
  pointsValueInCents?: number | null
  project: Project
  showImpactConversion?: boolean
  showOrganizationLogo?: boolean
  hideImpactBox?: boolean
}

export function Container({
  children = null,
  className = '',
  locale,
  pointsMode = false,
  pointsValueInCents = null,
  project,
  showImpactConversion = false,
  showOrganizationLogo = true,
  hideImpactBox = false,
}: ContainerProps) {
  const t = i18n[locale] ?? i18n.fr
  const hasContribution = project.contributors_count > 0
  const hasComments = showImpactConversion ? false : project.comment_count > 0

  const shouldDisplayInfos =
    hasContribution || hasComments || showImpactConversion

  return (
    <div className={tm('flex flex-col gap-y-2', className)}>
      {showOrganizationLogo ? (
        <ProjectOrganizationLogo project={project} />
      ) : null}

      {!hideImpactBox ? (
        <div className="flex flex-col gap-y-2">
          <div
            className={tm(
              'relative flex h-auto w-full items-center justify-between gap-4 overflow-hidden rounded-2xl border-2 border-[#F4F3E2] bg-gradient-to-r from-[#F4F3E2] from-10% to-[#EFECBD] px-6 py-3',
              shouldDisplayInfos && 'mb-1',
              project.is_done &&
                'border-[#EDFDEB] from-[#EDFDEB] from-10% to-[#B3E9BD]',
            )}
          >
            <div className="z-[1] flex flex-col items-start gap-y-1">
              {children}
            </div>
            <Ficello
              variant="scoubi"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={tm(
                'absolute -right-[60px] -top-[40px] h-auto w-[200px] stroke-[#E5E19F]',
                project.is_done && 'stroke-[#D1F7DC]',
              )}
            />
          </div>
        </div>
      ) : null}

      {shouldDisplayInfos ? (
        <div className="flex w-full items-center justify-between gap-x-2 text-dark-gray">
          {hasContribution ? (
            <ContributionsCount
              count={project.contributors_count}
              locale={locale}
              textClassName="text-sm"
              withSuffix
            />
          ) : null}

          {showImpactConversion ? (
            <ProjectImpactConversion
              project={project}
              locale={locale}
              pointsMode={pointsMode}
              pointsValueInCents={pointsValueInCents}
              className={tm(
                'w-full text-left text-sm',
                hasContribution && 'pr-3 text-right',
              )}
            />
          ) : null}

          {hasComments ? (
            <div className="flex items-center gap-x-1 whitespace-nowrap text-sm">
              <Icon icon="comment" size="small" />
              <div>
                {project.comment_count} {t.comments(project.comment_count)}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
